import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getUserDomains } from '../../utils/subdomainUtils';

const navItems = [
  { type: 'divider', label: 'Website' },
  { label: 'Edit Website', path: '/dashboard/website', icon: 'fa-solid fa-globe', exact: true },
  { label: 'Social & Chess Links', path: '/dashboard/website/social-links', icon: 'fa-solid fa-share-alt' },
  { label: 'Blog', path: '/dashboard/website/blogs', icon: 'fa-solid fa-newspaper' },
  { label: 'Contact Form', path: '/dashboard/website/contact', icon: 'fa-solid fa-envelope' },
  { label: 'Subscribers', path: '/dashboard/website/subscribers', icon: 'fa-solid fa-users' },

  { type: 'divider', label: 'Business' },
  { label: 'Academy', path: '/dashboard/coach', icon: 'fa-solid fa-chalkboard-user' },
  { label: 'Library', path: '/dashboard/library', icon: 'fa-solid fa-book' },
  { label: 'Shop', path: '/dashboard/website/shop', icon: 'fa-solid fa-store' },
  { label: 'Tournaments', path: '/dashboard/tournaments', icon: 'fa-solid fa-trophy' },
  { label: 'Donations', path: '/dashboard/website/donations', icon: 'fa-solid fa-heart' },
  { label: 'Revenue', path: '/dashboard/revenue', icon: 'fa-solid fa-dollar-sign' },
  { label: 'Analytics', path: '/dashboard/analytics', icon: 'fa-solid fa-chart-line' },
  { label: 'Referrals', path: '/dashboard/website/referrals', icon: 'fa-solid fa-users' },

  { type: 'divider', label: 'Account' },
  { label: 'Premium / Plan', path: '/dashboard/website/premium', icon: 'fa-solid fa-crown' },
  { label: 'Payments', path: '/dashboard/stripe', icon: 'fa-solid fa-credit-card' },
  { label: 'Settings', path: '/dashboard/settings', icon: 'fa-solid fa-gear' },
];

const DashboardSidebar = ({ isOpen, onClose, isPremium, primaryUsername, userProfile, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleNavClick = (item) => {
    if (item.external) {
      navigate(item.path);
    }
    // Close drawer on mobile
    onClose();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-[#111111] border-r border-gray-800
          flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Brand / Logo area */}
        <div className="px-5 py-5 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <button
              onClick={() => { navigate('/dashboard'); onClose(); }}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-chess-green/20 flex items-center justify-center">
                <i className="fas fa-chess-king text-chess-green text-sm"></i>
              </div>
              <span className="text-white font-bold text-lg">ChessWeb.site</span>
            </button>
            {/* Close button - mobile only */}
            <button
              onClick={onClose}
              className="lg:hidden p-1 text-gray-400 hover:text-white"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
          </div>
          {/* Profile info */}
          {primaryUsername && (
            <div className="mt-3 text-xs text-gray-500 truncate">
              {getUserDomains(primaryUsername, { isPremium }).primaryLabel}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {navItems.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key={idx} className={`${idx === 0 ? '' : 'mt-4'} mb-2 px-3`}>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    {item.label}
                  </span>
                </div>
              );
            }

            const active = isActive(item);
            const needsPremium = !isPremium && (
              item.path === '/dashboard/coach' ||
              item.path === '/dashboard/library' ||
              item.path === '/dashboard/website/shop' ||
              item.path === '/dashboard/tournaments' ||
              item.path === '/dashboard/website/donations' ||
              item.path === '/dashboard/revenue' ||
              item.path === '/dashboard/analytics'
            );

            if (item.external) {
              return (
                <button
                  key={idx}
                  onClick={() => handleNavClick(item)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-colors mb-0.5 text-left
                    text-gray-400 hover:text-white hover:bg-gray-800/60
                  `}
                >
                  <i className={`${item.icon} w-5 text-center text-gray-500`}></i>
                  <span className="flex-1">{item.label}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-gray-600"></i>
                </button>
              );
            }

            return (
              <NavLink
                key={idx}
                to={item.path}
                end={item.exact}
                onClick={() => onClose()}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors mb-0.5
                  ${active
                    ? 'bg-chess-green/10 text-chess-green'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/60'
                  }
                `}
              >
                <i className={`${item.icon} w-5 text-center ${active ? 'text-chess-green' : 'text-gray-500'}`}></i>
                <span className="flex-1">{item.label}</span>
                {needsPremium && (
                  <span className={`text-[10px] ${item.path === '/dashboard/tournaments' ? 'text-chess-green' : 'text-yellow-500'}`}>
                    <i className="fa-solid fa-crown"></i>
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {primaryUsername && (
          <div className="p-3 border-t border-gray-800">
            <a
              href={getUserDomains(primaryUsername, { isPremium }).primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800/60 transition-colors"
            >
              <i className="fa-solid fa-eye w-5 text-center text-gray-500"></i>
              <span>View My Site</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-gray-600"></i>
            </a>
          </div>
        )}
      </aside>
    </>
  );
};

export default DashboardSidebar;
